import {objectFromEntries} from '@benchling/js-utils';

import {Pagination} from 'lib/Listings';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  PlateMaterialInput,
  PlateMap,
  PlateMapWell,
  ReagentAssignmentGroup,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import {getSchematizedAttributeTagFromWell} from 'samples/plate-maps/PlateMapUtil';
import {
  DetailRowViewArgs,
  getDetailViewRow,
  getDetailViewRowArgs,
} from 'samples/plate-maps/transfer-table/DetailTransferTableUtil';
import {
  AnonymousTagValueTransferTableCell,
  FillPlateTransferTableViewMode,
  PlateRecordViewRowContents,
  PlateRecordViewTransferTableColumnKey,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';

/**
 * Builds a map from schematized field name to table cell within one plate record detail table row.
 * @param metadata A list of schematized field metadata. Can represent either schema fields or data parameters
 *                 so long as all metadata objects are the same type.
 * @param well Plate well to extract schema tag from.
 * @param wellMergeSpan Merge span for this well.
 */
export function reduceTagValueMetadataToCells(
  metadata: Array<SchematizedDisplayLayerMetadata>,
  well: PlateMapWell,
  wellMergeSpan: number | null
): {[key: string]: AnonymousTagValueTransferTableCell} {
  return objectFromEntries<AnonymousTagValueTransferTableCell>(
    metadata.map((item) => [
      item.displayName,
      {
        text: '',
        typedValue: {
          type: BaseTypedValueType.JSON,
          jsonValue: {
            tag: getSchematizedAttributeTagFromWell(well, item.attribute),
          },
        },
        ...(wellMergeSpan != null ? {mergeRowSpan: wellMergeSpan} : {}),
      },
    ])
  );
}

export function getPlateRecordViewRowsContents({
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  showMaterialInputColumn,
  plateDataParameterMetadata,
  wellSchemaFieldMetadata,
}: {
  pagination: Pagination;
  plateMap: PlateMap;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  selectedWellIndices: Set<number>;
  showMaterialInputColumn: boolean;
  plateDataParameterMetadata: Array<SchematizedDisplayLayerMetadata>;
  wellSchemaFieldMetadata: Array<SchematizedDisplayLayerMetadata>;
}): Array<PlateRecordViewRowContents> {
  const detailRowViewArgs = getDetailViewRowArgs({
    pagination,
    plateMap,
    plateMaterialInputs,
    reagentGroupsById,
    selectedWellIndices,
    showMaterialInputColumn,
    viewMode: FillPlateTransferTableViewMode.PLATE_RECORD,
  });

  return detailRowViewArgs.map(
    ({
      container,
      containerMergeSpan,
      materialInput,
      plateMaterialInputs,
      reagent,
      reagentGroupsById,
      viewMode,
      well,
      wellIndex,
      wellMergeSpan,
    }: DetailRowViewArgs<FillPlateTransferTableViewMode.PLATE_RECORD>) => ({
      ...getDetailViewRow({
        container,
        containerMergeSpan,
        materialInput,
        plateMaterialInputs,
        reagent,
        reagentGroupsById,
        showMaterialInputColumn,
        viewMode,
        well,
        wellIndex,
        wellMergeSpan,
      }),
      [PlateRecordViewTransferTableColumnKey.EXPERIMENTAL_ROLE]: {
        text: '',
        typedValue: {
          type: BaseTypedValueType.JSON,
          jsonValue: {
            experimentalRole: well.role,
          },
        },
        ...(wellMergeSpan != null ? {mergeRowSpan: wellMergeSpan} : {}),
      },
      [PlateRecordViewTransferTableColumnKey.SOURCE_CONTAINER]: {
        text: '',
        typedValue: {
          type: BaseTypedValueType.JSON,
          jsonValue: {
            reagent: container ?? reagent,
          },
        },
        ...(containerMergeSpan != null ? {mergeRowSpan: containerMergeSpan} : {}),
      },
      schemaTagColumns: reduceTagValueMetadataToCells(wellSchemaFieldMetadata, well, wellMergeSpan),
      dataParameterColumns: reduceTagValueMetadataToCells(plateDataParameterMetadata, well, wellMergeSpan),
    })
  );
}
